import DashboardEmptyState from './DashboardEmptyState';

const chartWidth = 1120;
const chartHeight = 430;
const margin = {
  top: 72,
  right: 24,
  bottom: 64,
  left: 78,
};

const yAxisTicks = [0, 50000, 100000, 150000, 200000];

function SalesAnalytics({ items }) {
  if (!items.length) {
    return (
      <div>
        <div className="mb-5 border-b border-[#efe6de] pb-5 text-center lg:text-left">
          <p className="m-0 text-[0.78rem] font-semibold uppercase tracking-[0.09em] text-[var(--theme-text-muted)]">
            Release Analytics
          </p>
          <h3 className="m-0 mt-2 text-[1.05rem] font-medium tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.2rem] lg:text-[1.32rem]">
            Release momentum by volume and activity
          </h3>
        </div>
        <DashboardEmptyState message="Release analytics will appear here once live release data is connected." />
      </div>
    );
  }

  const innerWidth = chartWidth - margin.left - margin.right;
  const innerHeight = chartHeight - margin.top - margin.bottom;
  const maxValue = 200000;
  const latestItem = items[items.length - 1];
  const latestVariance = latestItem.actual - latestItem.expected;

  const expectedPoints = items.map((item, index) =>
    toPoint(index, item.expected, items.length, innerWidth, innerHeight, maxValue)
  );
  const actualPoints = items.map((item, index) =>
    toPoint(index, item.actual, items.length, innerWidth, innerHeight, maxValue)
  );

  return (
    <div>
      <div className="mb-5 flex flex-col gap-4 border-b border-[#efe6de] pb-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="text-center lg:text-left">
          <p className="m-0 text-[0.78rem] font-semibold uppercase tracking-[0.09em] text-[var(--theme-text-muted)]">
            Release Analytics
          </p>
          <h3 className="m-0 mt-2 text-[1.05rem] font-medium tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.2rem] lg:text-[1.32rem]">
            Release momentum by volume and activity
          </h3>

          <div className="mt-3 flex flex-wrap items-center justify-center gap-5 text-[0.86rem] text-[var(--theme-text-subtle)] sm:text-[0.9rem] lg:justify-start">
            <LegendItem color="#b6b2ae" label="Expected Income" />
            <LegendItem color="#8f4108" label="Actual Income" />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-3 lg:min-w-[380px]">
          <AnalyticsMiniStat
            label="Dec Expected"
            value={formatCurrency(latestItem.expected)}
            tone="neutral"
          />
          <AnalyticsMiniStat
            label="Dec Actual"
            value={formatCurrency(latestItem.actual)}
            tone="accent"
          />
          <AnalyticsMiniStat
            label="Variance"
            value={`${latestVariance >= 0 ? '+' : ''}${formatCurrency(latestVariance)}`}
            tone={latestVariance >= 0 ? 'positive' : 'negative'}
          />
        </div>
      </div>

      <div className="-mx-2 w-[calc(100%+1rem)] overflow-x-auto px-2 sm:mx-0 sm:w-full sm:px-0">
        <svg
          className="min-w-[720px] sm:min-w-[860px] lg:min-w-[980px]"
          viewBox={`0 0 ${chartWidth} ${chartHeight}`}
          role="img"
          aria-label="Expected income and actual income across 2024"
        >
          <g transform={`translate(${margin.left}, ${margin.top})`}>
            {yAxisTicks.map((tick) => {
              const y = getY(tick, innerHeight, maxValue);

              return (
                <g key={tick}>
                  <line x1="0" x2={innerWidth} y1={y} y2={y} stroke="#d7cfc8" strokeWidth="1.2" />
                  <text
                    x="-12"
                    y={y + 6}
                    textAnchor="end"
                    fontSize="12"
                    fill="#554b44"
                  >
                    {formatCurrency(tick)}
                  </text>
                </g>
              );
            })}

            <path
              d={buildLinePath(expectedPoints)}
              fill="none"
              stroke="#b6b2ae"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            <path
              d={buildSmoothPath(actualPoints)}
              fill="none"
              stroke="#8f4108"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {expectedPoints.map((point, index) => (
              <circle
                key={`expected-${items[index].id}`}
                cx={point.x}
                cy={point.y}
                r="5.5"
                fill="#b6b2ae"
              />
            ))}

            {actualPoints.map((point, index) => (
              <circle
                key={`actual-${items[index].id}`}
                cx={point.x}
                cy={point.y}
                r="5.5"
                fill="#8f4108"
              />
            ))}

            {items.map((item, index) => (
              <text
                key={item.id}
                x={expectedPoints[index].x}
                y={innerHeight + 24}
                textAnchor="middle"
                fontSize="11"
                fill="#3f3833"
              >
                {formatShortLabel(item.label)}
              </text>
            ))}
          </g>
        </svg>
      </div>
    </div>
  );
}

function LegendItem({ color, label }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="h-3.5 w-3.5 rounded-full" style={{ backgroundColor: color }} />
      <span>{label}</span>
    </div>
  );
}

function AnalyticsMiniStat({ label, value, tone }) {
  const toneClassName =
    tone === 'accent'
      ? 'bg-[#f7efe7] text-[#7b3504]'
      : tone === 'positive'
        ? 'bg-[#edf5ee] text-[#537b59]'
        : tone === 'negative'
          ? 'bg-[#f7ece9] text-[#92544a]'
          : 'bg-[#f4f1ed] text-[#6d6259]';

  return (
    <div className="rounded-[16px] border border-[var(--theme-border-soft)] bg-white px-3.5 py-3 sm:px-4">
      <p className="m-0 text-[0.76rem] font-semibold uppercase tracking-[0.08em] text-[#a09085]">
        {label}
      </p>
      <strong className={`mt-2 inline-flex rounded-full px-3 py-1.5 text-[0.84rem] font-semibold sm:text-[0.95rem] ${toneClassName}`}>
        {value}
      </strong>
    </div>
  );
}

function toPoint(index, value, totalItems, innerWidth, innerHeight, maxValue) {
  const x =
    totalItems === 1 ? innerWidth / 2 : (index / (totalItems - 1)) * innerWidth;

  return {
    x,
    y: getY(value, innerHeight, maxValue),
  };
}

function getY(value, innerHeight, maxValue) {
  return innerHeight - (value / maxValue) * innerHeight;
}

function buildLinePath(points) {
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
}

function buildSmoothPath(points) {
  if (points.length < 2) {
    return '';
  }

  let path = `M ${points[0].x} ${points[0].y}`;

  for (let index = 0; index < points.length - 1; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const controlX = (current.x + next.x) / 2;

    path += ` C ${controlX} ${current.y}, ${controlX} ${next.y}, ${next.x} ${next.y}`;
  }

  return path;
}

function formatCurrency(value) {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatShortLabel(value) {
  return value.replace(', 2024', '');
}

export default SalesAnalytics;
